/**
 * The scorecard tab.
 *
 * Fetches on its own rather than with the startup: most visits to a detail page
 * do not open this tab, and the criteria configuration is a second query the
 * overview does not need.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 6.6
 */
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../../lib/auth-context';
import { useScorecard } from '../api/startups.queries';

import { ScorecardEditor } from './ScorecardEditor';

export interface ScorecardTabProps {
  startupId: string;
}

export function ScorecardTab({ startupId }: ScorecardTabProps): React.JSX.Element {
  const { t } = useTranslation();
  const { can } = useAuth();
  const { data, isPending, isError, refetch } = useScorecard(startupId);

  if (isPending) {
    return (
      <p role="status" className="text-body text-sm">
        {t('state.loading')}
      </p>
    );
  }

  if (isError) {
    return (
      <p role="alert" className="text-sem-bad text-sm">
        {t('errors.generic')}
      </p>
    );
  }

  return (
    <ScorecardEditor
      startupId={startupId}
      scorecard={data}
      canEdit={can('EDITOR')}
      /* Refetch rather than a page reload: the conflict is about one record, and
         reloading the page would lose the rest of the user's context. */
      onReload={() => {
        void refetch();
      }}
    />
  );
}
